import { useState, MouseEvent, useEffect, useRef } from "react";
import { createStaticRanges, DateRange, DateRangePicker, defaultStaticRanges, DefinedRange, RangeKeyDict } from "react-date-range";
import { Box, TextField, Popper, IconButton, Fade, Dialog, DialogContent, useMediaQuery, DialogActions, Button, Stack } from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { addDays } from "date-fns";
import { useTranslation } from "react-i18next";
import { dateLocales } from "../../../i18n";
import FormInput from "../form/FormInput";
import { StateAction } from "../../../utils/typeAlias";
import DateUtils from "../../../utils/dateUtils";
import moment from "moment";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

interface IDateRangeSelectProps {
  start: Date;
  setStart: StateAction<Date>;
  end: Date | undefined;
  setEnd: StateAction<Date | undefined>;
  showTime?: boolean;
}

const TODAY = new Date();
const MIN_DATE = new Date(2023, 0, 1);
const MAX_DATE = TODAY;

const DateRangeSelect = ({ start, setStart, end, setEnd, showTime = false }: IDateRangeSelectProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dateRange, setDateRange] = useState({
    selection: {
      startDate: start,
      endDate: end || MAX_DATE,
      key: "selection",
      color: theme.palette.primary.main,
    },
  });
  const [startTime, setStartTime] = useState<Date>(start);
  const [endTime, setEndTime] = useState<Date>(end || new Date());

  const onDateChange = (item: RangeKeyDict) => {
    if (moment(item.selection.endDate).isAfter(MAX_DATE)) {
      item.selection.endDate = MAX_DATE;
    }
    setDateRange({ ...dateRange, ...item });
    if (item.selection.endDate) {
      if (DateUtils.isToday(item.selection.endDate)) {
        setEndTime(new Date());
      } else {
        const newEndTime = new Date(item.selection.endDate);
        newEndTime.setHours(23, 59, 0, 0);
        setEndTime(newEndTime);
      }
    }
  }

  const defaultStaticRangeTranslations = {
    "Today": t("header.dateRangeSelect.today"),
    "Yesterday": t("header.dateRangeSelect.yesterday"),
    "This Week": t("header.dateRangeSelect.thisWeek"),
    "Last Week": t("header.dateRangeSelect.lastWeek"),
    "This Month": t("header.dateRangeSelect.thisMonth"),
    "Last Month": t("header.dateRangeSelect.lastMonth"),
  };

  const customStaticRanges = createStaticRanges([
    ...defaultStaticRanges.map((range) => ({
      ...range,
      label: defaultStaticRangeTranslations[range.label as keyof typeof defaultStaticRangeTranslations] || range.label,
    })),
    {
      label: t("header.dateRangeSelect.last7Days"),
      range: () => ({
        startDate: addDays(TODAY, -7),
        endDate: TODAY,
      }),
    },
    {
      label: t("header.dateRangeSelect.last30Days"),
      range: () => ({
        startDate: addDays(TODAY, -30),
        endDate: TODAY,
      }),
    },
  ]);

  const onOpen = (event: MouseEvent<HTMLElement>) => {
    if (isOpen) return onClose();

    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };

  const onClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
  };

  const onConfirm = () => {
    const startDate = new Date(dateRange.selection.startDate);
    if (showTime) {
      startDate.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
    }
    setStart(startDate);

    const endDate = new Date(dateRange.selection.endDate);
    if (showTime) {
      endDate.setHours(endTime.getHours(), endTime.getMinutes(), 59, 999);
    } else {
      endDate.setHours(23, 59, 59, 999);
    }
    setEnd(endDate);
    onClose();
  }

  const formatDisplayValue = () => {
    const startStr = dateRange.selection.startDate.toLocaleDateString();
    const endStr = dateRange.selection.endDate.toLocaleDateString();
    if (!showTime) return `${startStr} - ${endStr}`;
    const startTimeStr = moment(startTime).format("HH:mm");
    const endTimeStr = moment(endTime).format("HH:mm");
    return `${startStr} ${startTimeStr} - ${endStr} ${endTimeStr}`;
  };

  useEffect(() => {
    const onClickOutsideComponent = (event: Event) => {
      const target = event.target as Node;
      if (
        (popperRef.current && popperRef.current.contains(target)) ||
        (textFieldRef.current && textFieldRef.current.contains(target)) ||
        Array.from(document.querySelectorAll(".MuiPickersPopper-root, .MuiDialog-root")).some((el) => el.contains(target))
      )
        return;

      onClose();
    };

    if (!isMobile) {
      document.addEventListener("mousedown", onClickOutsideComponent);
    }
    return () => {
      if (!isMobile) {
        document.removeEventListener("mousedown", onClickOutsideComponent);
      }
    };
  }, [isMobile]);

  const timePickerSlotProps = {
    popper: { disablePortal: true },
  };

  const endDateIsToday = DateUtils.isToday(dateRange.selection.endDate);

  const timePickerRow = (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocales.get(i18n.language)}>
      <Stack direction="row" spacing={2} sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
        <TimePicker
          label={t("header.from")}
          value={startTime}
          onChange={(val) => val && setStartTime(val)}
          ampm={false}
          sx={{ flex: 1 }}
          slotProps={timePickerSlotProps}
        />
        <TimePicker
          label={t("header.to")}
          value={endTime}
          onChange={(val) => val && setEndTime(val)}
          ampm={false}
          maxTime={endDateIsToday ? new Date() : undefined}
          sx={{ flex: 1 }}
          slotProps={timePickerSlotProps}
        />
      </Stack>
    </LocalizationProvider>
  );

  return (
    <Box>
      <FormInput label={t("header.dateRangeSelect.dateRange")}>
        <TextField
          ref={textFieldRef}
          fullWidth
          value={formatDisplayValue()}
          onClick={onOpen}
          slotProps={{
            input: {
              endAdornment: (
                <IconButton onClick={onOpen}>
                  <EventIcon />
                </IconButton>
              )
            }
          }}
        />
      </FormInput>

      {!isMobile && (
        <Popper
          ref={popperRef}
          open={isOpen}
          anchorEl={anchorEl}
          placement="bottom-start"
          transition
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={300}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  border: 1,
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  boxShadow: 3,
                }}
              >
                <DateRangePicker
                  onChange={onDateChange}
                  staticRanges={customStaticRanges}
                  inputRanges={[]}
                  minDate={MIN_DATE}
                  maxDate={MAX_DATE}
                  ranges={[dateRange.selection]}
                  locale={dateLocales.get(i18n.language)}
                />
                {showTime && timePickerRow}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mr: 1, mb: 1 }}>
                  <Button onClick={onConfirm} variant="contained" color="primary">
                    {t("header.ok")}
                  </Button>
                </Box>
              </Box>
            </Fade>
          )}
        </Popper>
      )}

      {isMobile && (
        <Dialog fullScreen open={isOpen} onClose={onClose}>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <DateRange
                ranges={[dateRange.selection]}
                onChange={onDateChange}
                minDate={MIN_DATE}
                maxDate={MAX_DATE}
                locale={dateLocales.get(i18n.language)}
              />

              <DefinedRange
                ranges={[dateRange.selection]}
                staticRanges={customStaticRanges}
                inputRanges={[]}
                onChange={onDateChange}
              />

              {showTime && timePickerRow}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onConfirm} variant="contained" color="primary">
              {t("header.ok")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default DateRangeSelect;
